import React, { useEffect, useState } from "react";
import { Bell, X, Smartphone } from "lucide-react";
import { pushSupported, getSubscription, subscribePush } from "../lib/push";

const DISMISS_KEY = "prenotoo_push_optin_dismissed_at";
const REPROMPT_DAYS = 30;

function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/**
 * PushOptInBanner — soft prompt to enable push notifications.
 *
 * Behaviour:
 *  - Hidden if push is unsupported, already subscribed, permission denied,
 *    or recently dismissed (last DISMISS_KEY < REPROMPT_DAYS old).
 *  - On iOS Safari that's not yet "Added to Home" → shows install hint instead
 *    of the activate button (Apple requires PWA install before push works).
 *  - Pass `force` to bypass dismiss memory (e.g. right after a waitlist signup
 *    where the value of the prompt is highest).
 *
 * Mount once per page where it's contextually useful (CustomerDashboard top,
 * after waitlist signup, etc.). Self-renders nothing if conditions don't apply.
 */
export default function PushOptInBanner({ force = false, hint = null, onClose }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const iOSNeedsInstall = isIOS() && !isStandalone();

  useEffect(() => {
    let cancelled = false;
    async function decide() {
      if (!pushSupported()) return;
      if (Notification.permission === "denied") return; // user blocked
      if (Notification.permission !== "granted") {
        // not asked yet — show banner unless dismissed recently
        if (!force) {
          const stored = localStorage.getItem(DISMISS_KEY);
          if (stored) {
            const ageMs = Date.now() - parseInt(stored, 10);
            if (!Number.isNaN(ageMs) && ageMs < REPROMPT_DAYS * 24 * 3600 * 1000) return;
          }
        }
        if (!cancelled) setShow(true);
        return;
      }
      // granted — only show if no active subscription on this browser
      const sub = await getSubscription();
      if (!cancelled && !sub) setShow(true);
    }
    decide();
    return () => { cancelled = true; };
  }, [force]);

  if (!show) return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    onClose && onClose();
  }

  async function activate() {
    setBusy(true);
    setErr("");
    try {
      await subscribePush();
      setShow(false);
      onClose && onClose();
    } catch (e) {
      setErr(e?.message || "Errore attivazione notifiche");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4 mb-4 flex items-start gap-3 backdrop-blur dark:border-blue-800/50 dark:bg-blue-950/40"
      data-testid="push-optin-banner"
    >
      <div className="h-10 w-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0">
        <Bell className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-blue-900 dark:text-blue-200">
          {iOSNeedsInstall ? "Installa l'app per ricevere le notifiche" : "Attiva le notifiche"}
        </div>
        <p className="text-xs text-blue-800 mt-0.5 dark:text-blue-300">
          {hint || (iOSNeedsInstall
            ? "Apri Safari, tocca Condividi e poi “Aggiungi a Home”. Da lì ricevi avviso istantaneo quando si libera uno slot."
            : "Ricevi un avviso istantaneo sul telefono quando si libera uno slot, c'è una conferma o un promemoria — anche con Resend non configurato.")}
        </p>
        {err && <div className="text-xs text-rose-700 mt-1 dark:text-rose-300">{err}</div>}
        <div className="mt-2 flex flex-wrap gap-2">
          {!iOSNeedsInstall && (
            <button
              onClick={activate}
              disabled={busy}
              className="h-8 px-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="push-optin-activate"
            >
              <Bell className="h-3 w-3" />
              {busy ? "Attivo…" : "Attiva ora"}
            </button>
          )}
          {iOSNeedsInstall && (
            <span className="h-8 px-3 rounded-full bg-blue-600/10 text-blue-700 text-xs font-semibold inline-flex items-center gap-1.5 dark:text-blue-300">
              <Smartphone className="h-3 w-3" />
              Aggiungi a Home (iPhone)
            </span>
          )}
          <button
            onClick={dismiss}
            className="h-8 px-3 rounded-full text-blue-700/80 hover:text-blue-900 text-xs font-medium dark:text-blue-300"
            data-testid="push-optin-dismiss"
          >
            Più tardi
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        className="h-7 w-7 rounded-full hover:bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 dark:text-blue-300 dark:bg-blue-950/40"
        aria-label="Chiudi"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
